import { FC, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePagination } from '@/Components/Hooks';
import { Layout } from '@/Components/Layouts';
import { Pagination, Select } from '@/Components/UI';
import { DictionaryKeysEnum } from '@/Services/api/methods/v2/Dictionary';
import { notificationsStoreSelectors } from '@/Store/notifications/notificationsState';

import { StationCard } from './StationCard';
import { StationsFilter } from './StationsFilter';
import { StationFiltersQueryNames } from './StationsFilter/stationsFiltersQueryNames';
import S from './styles.module.scss';

export const Notifications: FC = () => {
    const [query] = useSearchParams();

    const { changePage, currentPage, lastPage, nextPage, perPage, prevPage, process } = usePagination(6);

    const loadFilteredDictionaries = notificationsStoreSelectors.use.loadFilteredDictionaries();
    const updateFilters = notificationsStoreSelectors.use.updateFilters();
    const loadStations = notificationsStoreSelectors.use.loadStations();

    const filteredDictionaries = notificationsStoreSelectors.use.filteredDictionaries();
    const selectedFilters = notificationsStoreSelectors.use.selectedFilters();
    const stations = notificationsStoreSelectors.use.stations();

    const filtersFromQuery = useMemo(() => {
        return {
            companies_ids: query.get('company_id') ? [Number(query.get('company_id'))] : [],
            stores_ids: query.get('store_id') ? [Number(query.get('store_id'))] : [],
            store_groups_ids: query.get('store_group_id') ? [Number(query.get('store_group_id'))] : [],
        };
    }, [query]);

    const sanitizedFilters = useMemo(() => {
        const sanitized: Partial<typeof filtersFromQuery> = {};

        for (const key in filtersFromQuery) {
            const value = filtersFromQuery[key as keyof typeof filtersFromQuery];
            sanitized[key as keyof typeof filtersFromQuery] = value.length > 0 && !value.includes(-1) ? value : undefined;
        }

        return sanitized;
    }, [filtersFromQuery]);

    useEffect(() => {
        updateFilters(sanitizedFilters);
    }, [sanitizedFilters, updateFilters]);

    useEffect(() => {
        loadFilteredDictionaries();
    }, [selectedFilters, loadFilteredDictionaries]);

    useEffect(() => {
        loadStations(
            {
                companies_ids: query.get('company_id') ?? undefined,
                store_groups_ids: query.get('store_group_id') ?? undefined,
                stores_ids: query.get('store_id') ?? undefined,
                page: currentPage,
                per_page: perPage,
            },
            process
        );
    }, [query, currentPage, perPage, loadStations, process]);

    return (
        <Layout name="notifications" breadcrumbs={[{ name: 'Notifications', current: true }]} nobg>
            <div className={S.header}>
                <h1>Notifications</h1>
                <div className={S.filters}>
                    <StationsFilter
                        dictionaryKey={DictionaryKeysEnum.COMPANIES}
                        queryName={StationFiltersQueryNames.COMPANY}
                        placeholder="Company"
                        label="Company"
                    >
                        {filteredDictionaries?.companies.map((company) => (
                            <Select.Option id={Number(company.value)} key={company.value}>
                                {company.label}
                            </Select.Option>
                        ))}
                    </StationsFilter>
                    <StationsFilter
                        dictionaryKey={DictionaryKeysEnum.STORE_GROUPS}
                        queryName={StationFiltersQueryNames.STORE_GROUP}
                        placeholder="Store group"
                        label="Store group"
                    >
                        {filteredDictionaries?.store_groups.map((storeGroup) => (
                            <Select.Option id={Number(storeGroup.value)} key={storeGroup.value}>
                                {storeGroup.label}
                            </Select.Option>
                        ))}
                    </StationsFilter>
                    <StationsFilter dictionaryKey={DictionaryKeysEnum.STORES} queryName={StationFiltersQueryNames.STORE} placeholder="Store" label="Store">
                        {filteredDictionaries?.stores.map((store) => (
                            <Select.Option id={Number(store.value)} key={store.value}>
                                {store.label}
                            </Select.Option>
                        ))}
                    </StationsFilter>
                </div>
            </div>
            <div className={S.content}>
                {stations.map((station) => (
                    <StationCard station={station} key={station.id} />
                ))}
            </div>
            {lastPage && lastPage > 1 && (
                <Pagination current={currentPage} last={lastPage ?? 1} prevPage={prevPage} nextPage={nextPage} changePage={changePage} />
            )}
        </Layout>
    );
};
